package com.schoolconnect.app.datasource.network.response

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class TeacherTimetableClassResponse(
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null
)

@Serializable
data class WeeklyTimetableResponse(
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null,
    @SerialName("week_start") val weekStart: String? = null,
    @SerialName("week_end") val weekEnd: String? = null,
    @SerialName("has_published_timetable") val hasPublishedTimetable: Boolean = false,
    @SerialName("is_published") val isPublished: Boolean = false,
    @SerialName("special_schedule_days") val specialScheduleDays: List<Int>? = null,
    @SerialName("weekly_off_days") val weeklyOffDays: List<Int>? = null,
    val days: List<WeeklyTimetableDayResponse>? = null,
    @SerialName("regular_slots") val regularSlots: List<WeeklyTimetableSlotResponse>? = null,
    @SerialName("special_slots") val specialSlots: List<WeeklyTimetableSlotResponse>? = null
)

@Serializable
data class WeeklyTimetableDayResponse(
    val date: String? = null,
    @SerialName("day_name") val dayName: String? = null,
    @SerialName("is_special_day") val isSpecialDay: Boolean = false,
    @SerialName("is_off_day") val isOffDay: Boolean = false,
    val cells: List<WeeklyTimetableDayCellResponse>? = null
)

@Serializable
data class WeeklyTimetableSlotResponse(
    @SerialName("schedule_type") val scheduleType: String? = null,
    @SerialName("slot_order") val slotOrder: Int = 0,
    @SerialName("from_time") val fromTime: String? = null,
    @SerialName("to_time") val toTime: String? = null,
    @SerialName("regular_day_cells") val regularDayCells: List<WeeklyTimetableDayCellResponse>? = null,
    @SerialName("special_day_cells") val specialDayCells: List<WeeklyTimetableDayCellResponse>? = null
)

@Serializable
data class WeeklyTimetableDayCellResponse(
    @SerialName("entry_text") val entryText: String? = null,
    @SerialName("is_holiday") val isHoliday: Boolean = false,
    @SerialName("is_special_day") val isSpecialDay: Boolean = false,
    @SerialName("is_off_day") val isOffDay: Boolean = false
)

@Serializable
data class TimetableClassHeaderResponse(
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null,
    val status: String? = null,
    @SerialName("published_till") val publishedTill: String? = null,
    val id: String? = null,
    @SerialName("school_id") val schoolId: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null,
    @SerialName("updated_by_id") val updatedById: String? = null,
    @SerialName("updated_by_name") val updatedByName: String? = null
)

@Serializable
data class ParentTimetableWeekResponse(
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null,
    @SerialName("week_start") val weekStart: String? = null,
    @SerialName("week_end") val weekEnd: String? = null,
    @SerialName("has_published_timetable") val hasPublishedTimetable: Boolean? = null,
    @SerialName("is_published") val isPublished: Boolean? = null,
    @SerialName("special_schedule_days") val specialScheduleDays: List<Int>? = null,
    @SerialName("weekly_off_days") val weeklyOffDays: List<Int>? = null,
    val days: List<WeeklyTimetableDayResponse>? = null,
    @SerialName("regular_slots") val regularSlots: List<WeeklyTimetableSlotResponse>? = null,
    @SerialName("special_slots") val specialSlots: List<WeeklyTimetableSlotResponse>? = null,
    // Keep the current parent payload readable during the backend response transition.
    @SerialName("regular_grid") val regularGrid: List<TimetableViewGridRowResponse> = emptyList(),
    @SerialName("special_grid") val specialGrid: List<TimetableViewGridRowResponse> = emptyList()
)

@Serializable
data class TimetableViewGridRowResponse(
    @SerialName("time_range") val timeRange: String = "",
    @SerialName("slot_order") val slotOrder: Int = 0,
    val cells: List<TimetableViewGridCellResponse> = emptyList()
)

@Serializable
data class TimetableViewGridCellResponse(
    @SerialName("day_of_week") val dayOfWeek: Int = 0,
    @SerialName("day_name") val dayName: String = "",
    @SerialName("entry_text") val entryText: String? = null,
    @SerialName("is_holiday") val isHoliday: Boolean = false,
    @SerialName("is_special_day") val isSpecialDay: Boolean = false,
    @SerialName("is_off_day") val isOffDay: Boolean = false
)

@Serializable
data class TimetableViewResponse(
    val id: String? = null,
    @SerialName("school_id") val schoolId: String? = null,
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null,
    val status: String? = null,
    @SerialName("published_till") val publishedTill: String? = null,
    @SerialName("from_date") val fromDate: String? = null,
    @SerialName("to_date") val toDate: String? = null,
    @SerialName("special_schedule_days") val specialScheduleDays: List<Int> = emptyList(),
    @SerialName("weekly_off_days") val weeklyOffDays: List<Int> = emptyList(),
    val slots: List<TimetableViewSlotResponse> = emptyList(),
    @SerialName("regular_grid") val regularGrid: List<TimetableViewGridRowResponse> = emptyList(),
    @SerialName("special_grid") val specialGrid: List<TimetableViewGridRowResponse> = emptyList()
)

@Serializable
data class TimetableViewSlotResponse(
    @SerialName("schedule_type") val scheduleType: String? = null,
    @SerialName("slot_order") val slotOrder: Int = 0,
    @SerialName("from_time") val fromTime: String? = null,
    @SerialName("to_time") val toTime: String? = null
)
